<template>
  <button
    class="btn"
    :class="[variant && `btn--${variant}`, { 'btn--wide': wide }]"
    :style="span ? { gridColumn: `span ${span}` } : null"
    :aria-label="ariaLabel || label"
    @click="onClick"
  >
    <span class="btn__label">{{ label }}</span>
    <span
      v-for="r in ripples"
      :key="r.id"
      class="btn__ripple"
      :style="{ left: r.x + 'px', top: r.y + 'px' }"
    />
  </button>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  // 'operator' | 'special' | 'equals' | 'clear' | 'accent' | null
  variant: { type: String, default: null },
  span: { type: Number, default: 0 },
  wide: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
});

const emit = defineEmits(['press']);

const ripples = ref([]);

function onClick(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  const id = Date.now() + Math.random();
  ripples.value.push({
    id,
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  });
  // Nettoie le ripple après l'animation.
  setTimeout(() => {
    ripples.value = ripples.value.filter((r) => r.id !== id);
  }, 550);

  emit('press', props.label);
}
</script>

<style scoped>
.btn {
  position: relative;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--surface-solid) 65%, white);
  border-radius: var(--radius-sm);
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--surface-solid) 80%, white),
    var(--surface-solid)
  );
  color: var(--text);
  font-size: 1.35rem;
  font-weight: 600;
  height: 62px;
  cursor: pointer;
  box-shadow:
    0 5px 0 var(--key-shadow),
    0 8px 10px rgba(0, 0, 0, 0.28),
    inset 0 1px 1px rgba(255, 255, 255, 0.35);
  transition:
    background var(--transition),
    box-shadow var(--transition);
  user-select: none;
}

.btn:hover {
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--surface-solid) 82%, var(--accent)),
    var(--surface-solid)
  );
}

.btn:active {
  box-shadow:
    0 1px 0 var(--key-shadow),
    0 2px 3px rgba(0, 0, 0, 0.2),
    inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

.btn__label {
  position: relative;
  z-index: 1;
}

.btn--operator {
  background: linear-gradient(
    155deg,
    color-mix(in srgb, var(--operator) 75%, white),
    var(--operator)
  );
  color: var(--accent);
}

.btn--special {
  background: linear-gradient(
    155deg,
    color-mix(in srgb, var(--special) 22%, var(--surface-solid)),
    var(--surface-solid)
  );
  color: var(--special);
  font-size: 1.1rem;
}

.btn--clear {
  color: var(--danger);
}

.btn--accent {
  color: var(--accent-2);
  font-weight: 700;
}

.btn--equals {
  background: linear-gradient(135deg, var(--equals-1), var(--equals-2));
  color: #fff;
  box-shadow:
    0 5px 0 color-mix(in srgb, var(--equals-2) 60%, var(--case-edge)),
    0 9px 18px var(--glow),
    inset 0 1px 1px rgba(255, 255, 255, 0.48);
}

.btn--equals:hover {
  filter: brightness(1.08);
}

.btn__ripple {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: translate(-50%, -50%);
  animation: ripple 550ms ease-out forwards;
  pointer-events: none;
}

@keyframes ripple {
  to {
    width: 220px;
    height: 220px;
    opacity: 0;
  }
}

@media (max-width: 420px) {
  .btn {
    height: 54px;
    font-size: 1.2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn {
    transition: none;
  }
}
</style>
